
import {Box, Grid, Paper, Typography, Dialog, Button, DialogTitle, DialogContent, DialogActions} from "@material-ui/core";
import { IdentityApplication } from "libs/resources/identity/applications/PublisherApplicationService";
import React, { useState, useContext, useEffect } from "react";
import { SnackNotificationContext } from "legacy-components/notifications/GenericSnackNotification";
import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";
import Submit from "modus/components/form/submit/Submit";
import AddIcon from "@material-ui/icons/Add";
import GenericPageTitle from "modus/components/generic-page-title/GenericPageTitle";
import { ErrorMessage, resolveAPIErrorMessage } from "modus/components/error-message/ErrorMessage";
import { useAsyncAction } from "legacy-components/form/action/AsyncAction";
import { InputContainer } from "modus/components/form/input-container/InputContainer";
import { Alert, Skeleton } from "@material-ui/lab";
import InfoIcon from "@material-ui/icons/Info";
import { ScopeData } from "../../applications/pages/application-summary-page/ResourceServerConfig";
import { ScopesList } from "pages/home/applications/components/ScopesList";
import ApiService from "../services/ApiService";
import { EditScopeConfiguration } from "pages/home/applications/components/EditScopeConfiguration";
import { ScopeFilter, ScopeFilterValues } from "pages/home/components/scope-filter/ScopeFilter";
import {ResourceAuthorizer} from "../../authorizer/components/Authorizer";
import {useParams} from "react-router";
import {useAuthorization} from "../../authorizer/components/AuthorizationProvider";

interface ScopeConfigurationProps {
  application: IdentityApplication;
}

/**
 * Renders the configuration details of an application.
 *
 * @param {Object} props - The component props.
 * @param {IdentityApplication} props.application - The application object.
 * @returns {JSX.Element} - The rendered component.
 * @description  this component dynamically generates a list of configuration details
 * based on the application's responses and the current identity environment,
 * displaying them in a structured and styled manner.
 */
export const ScopeConfiguration: React.FC<ScopeConfigurationProps> = ({
  application,
}) => {
  const identityEnvironment = useSelector(
    (state: any) => state?.common?.identityEnvironment
  );

  const [scopeData, setScopeData] = React.useState<ScopeData[]>([]);
  const [filteredScopeData, setFilteredScopeData] = React.useState<ScopeData[]>([]);
  const notification = useContext(SnackNotificationContext);
  const [displayAddScope, setDisplayAddScope] = useState(false);
  const [displayEditScope, setDisplayEditScope] = useState(false);
  const [hasFieldError, setHasFieldError] = useState(false);
  const [hasFieldError2, setHasFieldError2] = useState(false); 
  const [selectedScope, setSelectedScope] = useState<ScopeData | null>(null);

  const [formValues, setFormValues] = useState({
    name: "",
    developerDocumentation: "",
    consentMessage: "",
    alwaysShowConsent: false
  });

  const consumerKey: string = (() => {
    const gatewayResponses = application.applicationGatewayResponses;
    if (!gatewayResponses || gatewayResponses.length === 0) {
      return "";
    }

    if (gatewayResponses[0]?.organisationName === identityEnvironment) {
      return gatewayResponses[0]?.consumerKey || "";
    } else {
      return gatewayResponses[1]?.consumerKey || "";
    }
  })();

  const handleSetError = (value: boolean) => {
    setHasFieldError(value); 
  };

  const handleSetError2 = (value: boolean) => {
    setHasFieldError2(value);
  };

  const isDynamicScope = (scopeName: string) => {
    const dynamicScopePattern = /^[A-Za-z0-9_-]+(\.[A-Za-z0-9_-]+)*:[^#]+#[^#]+$/;
    return dynamicScopePattern.test(scopeName);
  };

  const getHumanReadableName = (scopeName: string) => {
    const parts = scopeName.split('#');
    return parts.length > 1 ? parts[1] : '';
  };

  const validateConsentMessage = (message: string, scopeName: string) => {
    if (!message) return false;
    
    if (isDynamicScope(scopeName)) {
      const humanReadableName = getHumanReadableName(scopeName);
      if (!message.includes(`{{${humanReadableName}}}`)) {
        return false;
      }
    }
    return true;
  };

  useEffect(() => {
    if( scopeData.length >= 30) {
      setDisplayAddScope(false)
    }
    setFilteredScopeData(scopeData);
  },[scopeData]);

  const handleFilterChange = (filters: ScopeFilterValues) => {
    let filtered = [...scopeData];

    // Apply search filter
    if (filters.searchText) {
      filtered = filtered.filter(scope => 
        scope.scope.toLowerCase().includes(filters.searchText.toLowerCase()) ||
        (scope.developerDocumentation && scope.developerDocumentation.toLowerCase().includes(filters.searchText.toLowerCase()))
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;
      switch (filters.sortBy) {
        case 'name':
          comparison = a.scope.localeCompare(b.scope);
          break;
        case 'consentMessage':
          const aMessage = a.consentMessage || '';
          const bMessage = b.consentMessage || '';
          comparison = aMessage.localeCompare(bMessage);
          break;
        case 'alwaysShowConsent':
          // Sort by alwaysShowConsent (true values first)
          const aConsent = a.alwaysShowConsent || false;
          const bConsent = b.alwaysShowConsent || false;
          comparison = (aConsent === bConsent) ? 0 : aConsent ? -1 : 1;
          break;
      }
      return filters.sortDirection === 'asc' ? comparison : -comparison;
    });

    setFilteredScopeData(filtered);
  };

  const [fetchScopeState, { subscribe: fetchScopes }] = useAsyncAction(() =>
    ApiService.getScopes(consumerKey)
  );
  const [createScopeState, { subscribe: createScope }] = useAsyncAction(() =>
    ApiService.addScope(consumerKey, formValues.name, formValues.consentMessage, formValues.developerDocumentation, formValues.alwaysShowConsent)
  );

  React.useEffect(() => {
    if (consumerKey) {
      fetchScopes();
    }
  }, [consumerKey, fetchScopes]);

  React.useEffect(() => {
    if (fetchScopeState?.value) {
      setScopeData(fetchScopeState?.value);
    }
  }, [fetchScopeState.value]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormValues((prevValues) => {
      const newValues = {
        ...prevValues,
        [name]: value,
      };
      
      // Validate consent message when either scope name or consent message changes
      if (name === 'name' || name === 'consentMessage') {
        if (newValues.consentMessage) {
          validateConsentMessage(newValues.consentMessage, newValues.name);
        }
      }
      
      return newValues;
    });
  };

  const displayAddScopeInput = () => {
    setDisplayAddScope(true);
  };

  const handleEditScope = (scope: ScopeData) => {
    setSelectedScope(scope);
    setDisplayEditScope(true);
  };

  const addScope = () => {
    if (consumerKey !== "" && !hasFieldError && !hasFieldError2 && formValues.name !== "" && formValues.consentMessage !== "" && formValues.developerDocumentation !== "") {  
      if(!createScopeState.loading){
        createScope();
      }
    }
    else {
        notification.show({
            id: "addscopestate-error",
            message: <FormattedMessage defaultMessage="Invalid Input." />,
            timeout: 4000
        })
    }
  };

  useEffect(() => {
    if (!createScopeState) {
      return
    }
    if (createScopeState.error) {
      // Keep the dialog open when there's an error
      return;
    }
    if (createScopeState.loaded) {
      notification.show({
        id: "addscopestate-success",
        message: <FormattedMessage defaultMessage="Scope Added successfully." />,
        timeout: 4000
    })

    setFormValues({name: "",
      developerDocumentation: "",
      consentMessage: "",
      alwaysShowConsent: false
    });

    fetchScopes();
    setDisplayAddScope(false);
    }
  },[notification, createScopeState, fetchScopes]);

  const disableAction = () => {
    if(createScopeState?.loading) {
      return true;
    }
    // min length check
    if(formValues.name?.trim().length === 0 || 
       formValues.consentMessage.trim().length < 3 || 
       formValues.developerDocumentation?.trim().length < 3) {
      return true;
    }
    // max length check
    if(formValues.consentMessage.trim().length > 500 || 
       formValues.developerDocumentation?.trim().length > 500) {
      return true;
    }
    const scopePattern = /^[A-Za-z0-9_-]+(\.[A-Za-z0-9_-]+)*(?::[^#]+#[^#]+)?$/;
    if (!scopePattern.test(formValues.name)) {
      return true;
    }
    // Validate consent message format for dynamic scopes
    if (!validateConsentMessage(formValues.consentMessage, formValues.name)) {
      return true;
    }
    return false;
  }
  const { id } = useParams<{ id: string }>();
  const { isActionAllowed } = useAuthorization("APPLICATION" , id);
  const canUpdate = isActionAllowed("update");

  return (
    <Paper style={{ marginTop: "30px" }}>
      {consumerKey !== ""  && <Box p={"20px"}>
        <ResourceAuthorizer action={"update"} resourceType={"APPLICATION"} resourceId={id} hideErrorMessage={true}>

        <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center"}}>
          <GenericPageTitle title={ <Typography variant="body1" style={{ fontWeight: 600 }}>Scopes </Typography>} noSkeleton noDescription />
          <Submit size="medium" variant="contained" color="primary" style={{ marginLeft: "auto" }}
            startIcon={<AddIcon />}
            onClick={() => displayAddScopeInput()}
            disabled={displayAddScope}>
            Add New Scope
          </Submit>
        </Box>
        </ResourceAuthorizer>

          <Dialog open={displayAddScope} onClose={() => setDisplayAddScope(false)} maxWidth="md" fullWidth>
            <DialogTitle><b>Add New Scope</b></DialogTitle>
            <DialogContent>
          <Box p={2}>
            <Grid container spacing={4} >
              {createScopeState?.errorValue && (
                <Grid item xs={12}>
                  <Alert 
                    severity="error" 
                    variant="outlined"
                  >
                    {resolveAPIErrorMessage(createScopeState.errorValue, 
                     <FormattedMessage defaultMessage="An error occurred while creating the scope. Please try again." />)}
                  </Alert>
                </Grid>
              )}
              <Grid item xs={12}>
                <Alert severity="info" variant="outlined"  iconMapping={{ info: <InfoIcon color="primary" />}} >
                For configuring static scope, use the format
                        <i>&lt;resource_name&gt;[.&lt;subresource&gt;][.&lt;action&gt;]</i> <br/>
      For configuring dynamic scope, use the format
      <i>&lt;resource_name&gt;[.&lt;subresource&gt;][.&lt;action&gt;]:&lt;id&gt;#&lt;human_readable_name&gt;</i>
                </Alert>
              </Grid>
              <Grid item xs={12}>
                Scope Name <span className="required">*</span>
                <Box display="flex" flexDirection="row">
                  <Grid container  >
                    <Grid item xs={4}>
                      <InputContainer required={true} value={application.applicationName + ":"} disabled name={"application_name"} inputStyle={{ textAlign: "right" }} />
                    </Grid>
                    <Grid item xs={8}>
                      <InputContainer 
                        value={formValues.name} 
                        onChange={handleChange} 
                        name={"name"} 
                        placeholder="Scope Name" 
                        setError={handleSetError}
                        validate={{ 
                          required: true, 
                          minLength: 3, 
                          maxLength: 500, 
                          pattern: "^[A-Za-z0-9_-]+(\\.[A-Za-z0-9_-]+)*(?:\\:[^#]+#[^#]+)?$", 
                          patternMessage: "Scope name must be in format: resource[.subresource][.action] or resource[.subresource][.action]:id#name for dynamic scopes. Only alphanumeric, underscore, and hyphen allowed."
                        }}
                        required />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              <Grid item xs={12}>
                User Consent Message <span className="required">*</span>
                <Box display="flex" flexDirection="row" style={{gap: "14px"}}>
                  <InputContainer 
                  inputStyle={{height: "100px",padding: "12px 12px",}}
                  rows={4}
                  multiline={true}
                  value={formValues.consentMessage} 
                  onChange={handleChange} 
                  name={"consentMessage"} 
                  placeholder="Enter a short description on the scope for getting user consent" 
                  setError={handleSetError2}
                  validate={{ 
                    required: true, 
                    minLength: 3, 
                    maxLength: 200,
                    allowSpace: true,
                    pattern: isDynamicScope(formValues.name) 
                      ? `.*\\{\\{${getHumanReadableName(formValues.name)}\\}\\}.*`
                      : undefined,
                    patternMessage: isDynamicScope(formValues.name)
                      ? "For dynamic scopes, consent message must include {{file_name}}"
                      : undefined
                  }}
                  required />
                  
                </Box>
              </Grid>
              <Grid item xs={12}>
                Developer Documentation <span className="required">*</span>
                <Box display="flex" flexDirection="row" style={{gap: "14px"}}>
                  <InputContainer 
                    inputStyle={{height: "100px", padding: "12px 12px",}}
                    rows={4}
                    multiline={true}
                    value={formValues.developerDocumentation} onChange={handleChange} name={"developerDocumentation"} placeholder="Add a brief note on the scope for application developer understanding" setError={handleSetError2}
                    validate={{ required: true, minLength: 3, maxLength: 512, allowSpace: true }}
                    required />
                </Box>
                <Box display="flex" alignItems="center">
                  <input
                    type="checkbox"
                    id="alwaysShowConsent"
                    name="alwaysShowConsent"
                    checked={formValues.alwaysShowConsent}
                    onChange={(e) => setFormValues({...formValues, alwaysShowConsent: e.target.checked})}
                    style={{marginRight: "8px"}}
                  />
                  <label htmlFor="alwaysShowConsent">Always ask consent for this scope</label>
                </Box>
              </Grid>

            </Grid>
          </Box>
          </DialogContent>
          <DialogActions style={{padding: "20px 35px"}}>
            <Button onClick={() => setDisplayAddScope(false)} color="secondary" variant="outlined">
              Cancel
            </Button>
            <Box ml={1}/>
            <Button onClick={addScope} color="primary" disabled={disableAction()} variant="contained"
            >
              Create
            </Button>
          </DialogActions>
          </Dialog>

        <Box>
          {fetchScopeState.loading && 
            <Box marginTop={"24px"} marginBottom={"8px"}>
              <Skeleton animation="wave" width="100%" height="200px" />
            </Box>}
            
          {fetchScopeState.loaded && scopeData.length === 0 && canUpdate &&(
            <ErrorMessage message={<Box display="flex" flexDirection="column">Add a Scope</Box>}/>
          )}
          
          {fetchScopeState.loaded && !fetchScopeState.error && scopeData.length !== 0 && (
            <Box marginTop={"24px"} marginBottom={"8px"}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box flex={1}>
                  <ScopeFilter onFilterChange={handleFilterChange} />
                </Box>
              </Box>
              {filteredScopeData.length === 0 && (
                <ErrorMessage message=
                  "No scopes found for the given search criteria" />)}
              {filteredScopeData.length > 0 && (<ScopesList
                application_name={application.applicationName}
                data={filteredScopeData}
                onEdit={handleEditScope}
              />)}
            </Box>
          )}
        </Box>
      </Box>}

      {displayEditScope && selectedScope && (
        <Dialog
          open={displayEditScope}
          onClose={() => setDisplayEditScope(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle><b>Edit Scope</b></DialogTitle>
          <EditScopeConfiguration
            application={application}
            selectedScope={selectedScope}
            onClose={(wasUpdated = false) => {
              setDisplayEditScope(false);
              setSelectedScope(null);
              // Only refresh scopes if there was an update
              if (wasUpdated) {
                fetchScopes();
              }
            }}
          />
        </Dialog>
      )}
    </Paper>
  );
};
